import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import styles from './HomePage.module.css';
import { Helmet } from 'react-helmet';

const HomePage: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [openFaq, setOpenFaq] = useState<number | null>(null);

    const startNewChat = () => {
        navigate('/chat');
    };

    const scrollToSection = (sectionId: string) => {
        const section = document.getElementById(sectionId);
        if (section) {
            section.scrollIntoView({ behavior: 'smooth' });
        }
    };

    useEffect(() => {
        const hash = location.hash.replace('#', '');
        if (hash) {
            scrollToSection(hash);
        }
    }, [location]);

    const toggleFaq = (index: number) => {
        setOpenFaq(openFaq === index ? null : index);
    };

    const currentYear = new Date().getFullYear();

    return (
        <div className={styles.homeContainer}>
            <Helmet>
                <meta charSet="UTF-8" />
                <title>GPT4Chats - Лучший ИИ-помощник для работы и творчества</title>
                <meta
                    name="description"
                    content="GPT4Chats — универсальный ИИ-помощник на базе GPT-4 для генерации текстов, анализа документов и решения задач. Бесплатный доступ и PRO-функции."
                />
                <meta
                    name="keywords"
                    content="ИИ-помощник, GPT-4, генерация текста, анализ документов, GPT4Chats, искусственный интеллект, автоматизация задач"
                />
                <meta name="robots" content="index, follow" />
                <meta name="viewport" content="width=device-width, initial-scale=1.0" />
                <link rel="canonical" href="https://gpt4chats.ru/" />
                <meta property="og:title" content="GPT4Chats - Лучший ИИ-помощник для работы и творчества" />
                <meta
                    property="og:description"
                    content="Используйте GPT4Chats для генерации текстов, анализа документов и автоматизации задач. Бесплатно или с PRO-функциями на базе GPT-4."
                />
                <meta property="og:url" content="https://gpt4chats.ru/" />
                <meta property="og:type" content="website" />
                <meta property="og:site_name" content="GPT4Chats" />
            </Helmet>
            {/* Шапка */}
            <header className={styles.homeHeader}>
                <div className={styles.container}>
                    <div className={styles.headerContent}>
                        <div className={styles.homeLogo}>
                            <h1>
                                GPT4Chats<span>.</span>
                            </h1>
                        </div>
                        <nav className={styles.homeNav}>
                            <button className={styles.navLink} onClick={() => scrollToSection('hero')}>
                                Главная
                            </button>
                            <button className={styles.navLink} onClick={() => scrollToSection('features')}>
                                Особенности
                            </button>
                            <button className={styles.navLink} onClick={() => scrollToSection('benefits')}>
                                Преимущества
                            </button>
                            <button className={styles.navLink} onClick={() => scrollToSection('use-cases')}>
                                Примеры
                            </button>
                            <button className={styles.navLink} onClick={() => scrollToSection('faq')}>
                                Вопросы
                            </button>
                            <button className={styles.startChatBtnSmall} onClick={startNewChat}>
                                Начать
                            </button>
                        </nav>
                    </div>
                </div>
            </header>

            {/* Герой-секция */}
            <section id="hero" className={styles.heroSection}>
                <div className={styles.container}>
                    <h1>GPT4Chats — Лучший ИИ-помощник для ваших задач</h1>
                    <p>
                        GPT4Chats помогает решать задачи, генерировать идеи и получать ответы на любые вопросы
                    </p>
                    <button className={styles.startChatBtn} onClick={startNewChat}>
                        Попробовать сейчас <i className="fas fa-arrow-right"></i>
                    </button>
                </div>
            </section>

            {/* Особенности */}
            <section id="features" className={styles.featuresSection}>
                <div className={styles.container}>
                    <h2>GPT4Chats – ваш универсальный ИИ-ассистент</h2>
                    <div className={styles.featuresGrid}>
                        <div className={styles.featureCard}>
                            <i className="fas fa-brain"></i>
                            <h3>GPT-4 и нейросети нового поколения</h3>
                            <p>Продвинутый искусственный интеллект для сложных задач: от написания кода до креативного контента.</p>
                        </div>
                        <div className={styles.featureCard}>
                            <i className="fas fa-wallet"></i>
                            <h3>Бесплатный доступ + PRO-функции</h3>
                            <p>Работайте с базовым ИИ без ограничений или расширьте возможности через премиум-подписку.</p>
                        </div>
                        <div className={styles.featureCard}>
                            <i className="fas fa-cogs"></i>
                            <h3>ИИ под ваш стиль</h3>
                            <p>Настраивайте тон, стиль ответов. Персонализация для бизнеса и личных задач.</p>
                        </div>
                        <div className={styles.featureCard}>
                            <i className="fas fa-folder-open"></i>
                            <h3>Анализ PDF и документов</h3>
                            <p>Загружайте файлы (PDF, DOCX, CSV) для автоматической обработки данных и генерации отчетов.</p>
                        </div>
                        <div className={styles.featureCard}>
                            <i className="fas fa-copy"></i>
                            <h3>20+ ИИ-моделей в одном месте</h3>
                            <p>Выбирайте специализированные LLM под задачу: переводы, аналитика, диалоги или графики.</p>
                        </div>
                        <div className={styles.featureCard}>
                            <i className="fas fa-mobile-alt"></i>
                            <h3>Доступ на любом устройстве</h3>
                            <p>Кроссплатформенный сервис: веб-версия, приложения для iOS/Android и Telegram-бот с синхронизацией данных.</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Преимущества */}
            <section id="benefits" className={styles.benefitsSection}>
                <div className={styles.container}>
                    <h2>GPT4Chats — выгоднее и надежнее аналогов</h2>
                    <div className={styles.benefitsGrid}>
                        <div className={styles.featureCard}>
                            <i className="fas fa-piggy-bank"></i>
                            <h3>Платите только за реальное использование</h3>
                            <p>
                                Никаких подписок «всё включено»: токены списываются только когда вы
                                <strong> действительно генерируете контент</strong>. Экономия до 40%
                                против конкурентов.
                            </p>
                        </div>
                        <div className={styles.featureCard}>
                            <i className="fas fa-server"></i>
                            <h3>99.9% аптайм на гибридных серверах</h3>
                            <p>
                                Наш ИИ работает на <strong>GPU NVIDIA + AWS</strong>. Даже при
                                пиковых нагрузках вы не увидите «сервер перегружен».
                            </p>
                        </div>
                        <div className={styles.featureCard}>
                            <i className="fas fa-shield-halved"></i>
                            <h3>Данные под тройной защитой</h3>
                            <p>
                                <strong>Шифрование</strong> (AES-256),
                                <strong>автоудаление файлов</strong> через 24 часа и
                                <strong>регулярные аудиты</strong> безопасности.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Примеры использования */}
            <section id="use-cases" className={styles.useCasesSection}>
                <div className={styles.container}>
                    <h2>Как GPT4Chats помогает в жизни</h2>
                    <div className={styles.useCasesGrid}>
                        <div className={styles.useCaseCard}>
                            <i className="fas fa-chart-line"></i>
                            <h3>Автоматизация бизнеса</h3>
                            <p>
                                Генерация коммерческих предложений, анализ конкурентов
                                и AI-ассистент для встреч в календаре.
                            </p>
                        </div>
                        <div className={styles.useCaseCard}>
                            <i className="fas fa-robot"></i>
                            <h3>ИИ-репетитор</h3>
                            <p>
                                Разбор задач по математике, физике и программированию
                                с пошаговыми объяснениями.
                            </p>
                        </div>
                        <div className={styles.useCaseCard}>
                            <i className="fas fa-vr-cardboard"></i>
                            <h3>Генерация креатива</h3>
                            <p>
                                Сценарии для TikTok, идеи нейросетевого арта
                                и промты для Midjourney.
                            </p>
                        </div>
                        <div className={styles.useCaseCard}>
                            <i className="fas fa-coins"></i>
                            <h3>Управление финансами</h3>
                            <p>
                                Анализ трат через CSV, прогнозирование бюджета
                                и чек-листы для налоговых выплат.
                            </p>
                        </div>
                        <div className={styles.useCaseCard}>
                            <i className="fas fa-heartbeat"></i>
                            <h3>Здоровье и самочувствие</h3>
                            <p>
                                Подбор тренировок, расшифровка анализов
                                и персонализированные рецепты.
                            </p>
                        </div>
                        <div className={styles.useCaseCard}>
                            <i className="fas fa-passport"></i>
                            <h3>Путешествия</h3>
                            <p>
                                Маршруты, визовые чек-листы и реальные фразы
                                на 15 языках для поездок.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section id="faq" className={styles.faqSection}>
                <div className={styles.container}>
                    <h2>Ответы на популярные вопросы о GPT4Chats</h2>
                    <div className={styles.faqList}>
                        <div className={styles.faqItem}>
                            <div
                                className={`${styles.faqQuestion} ${openFaq === 0 ? styles.open : ''}`}
                                onClick={() => toggleFaq(0)}
                            >
                                Что такое GPT4Chats и как он работает?
                                <i className="fas fa-chevron-down"></i>
                            </div>
                            <div className={`${styles.faqAnswer} ${openFaq === 0 ? styles.open : ''}`}>
                                GPT4Chats — это современный ИИ-помощник, доступный на сайте GPT4Chats.ru. Он основан на передовых нейросетях, таких как GPT-4, и помогает пользователям решать задачи, отвечать на вопросы, генерировать тексты, анализировать документы и многое другое. Вы можете использовать бесплатные модели для базовых задач или подключить платные премиум-функции для более сложных запросов. Просто задайте вопрос или загрузите файл, и ИИ обработает ваш запрос за секунды!
                            </div>
                        </div>
                        <div className={styles.faqItem}>
                            <div
                                className={`${styles.faqQuestion} ${openFaq === 1 ? styles.open : ''}`}
                                onClick={() => toggleFaq(1)}
                            >
                                Как начать пользоваться GPT4Chats бесплатно?
                                <i className="fas fa-chevron-down"></i>
                            </div>
                            <div className={`${styles.faqAnswer} ${openFaq === 1 ? styles.open : ''}`}>
                                Чтобы начать работу с GPT4Chats бесплатно, перейдите на сайт GPT4Chats.ru, нажмите кнопку "Начать" и зарегистрируйтесь. После этого выберите одну из доступных бесплатных моделей ИИ. Никаких скрытых платежей — вы сразу можете задавать вопросы, генерировать тексты или анализировать файлы. Для расширенных возможностей, таких как обработка больших документов или доступ к GPT-4, можно пополнить баланс и использовать PRO модели.
                            </div>
                        </div>
                        <div className={styles.faqItem}>
                            <div
                                className={`${styles.faqQuestion} ${openFaq === 2 ? styles.open : ''}`}
                                onClick={() => toggleFaq(2)}
                            >
                                Чем отличаются бесплатные и платные модели GPT4Chats?
                                <i className="fas fa-chevron-down"></i>
                            </div>
                            <div className={`${styles.faqAnswer} ${openFaq === 2 ? styles.open : ''}`}>
                                Бесплатные модели GPT4Chats идеально подходят для повседневных задач: ответов на вопросы, написания текстов, простых переводов или генерации идей. Платные модели, такие как GPT-4 и специализированные LLM, предоставляют больше мощности и функций — например, анализ PDF-документов, сложные вычисления, персонализированные ответы и доступ к 20+ нейросетям. Выбирайте то, что подходит именно вам, на GPT4Chats.ru!
                            </div>
                        </div>
                        <div className={styles.faqItem}>
                            <div
                                className={`${styles.faqQuestion} ${openFaq === 3 ? styles.open : ''}`}
                                onClick={() => toggleFaq(3)}
                            >
                                Безопасно ли использовать GPT4Chats для личных данных?
                                <i className="fas fa-chevron-down"></i>
                            </div>
                            <div className={`${styles.faqAnswer} ${openFaq === 3 ? styles.open : ''}`}>
                                Да, GPT4Chats обеспечивает высокий уровень безопасности. Все данные шифруются с помощью AES-256, загруженные файлы автоматически удаляются через 24 часа, а система регулярно проходит аудиты безопасности. Вы можете спокойно использовать наш ИИ-помощник для работы с конфиденциальной информацией — ваши данные под надежной защитой на GPT4Chats.ru.
                            </div>
                        </div>
                        <div className={styles.faqItem}>
                            <div
                                className={`${styles.faqQuestion} ${openFaq === 4 ? styles.open : ''}`}
                                onClick={() => toggleFaq(4)}
                            >
                                Какие задачи можно решать с помощью GPT4Chats?
                                <i className="fas fa-chevron-down"></i>
                            </div>
                            <div className={`${styles.faqAnswer} ${openFaq === 4 ? styles.open : ''}`}>
                                GPT4Chats подходит для множества задач: написание текстов (статей, сценариев, постов), анализ документов (PDF, CSV), помощь в обучении (математика, программирование), генерация идей для творчества, переводы на 15+ языков, автоматизация бизнеса и даже планирование путешествий. Это универсальный ИИ-помощник, который адаптируется под ваши нужды. Попробуйте на GPT4Chats.ru!
                            </div>
                        </div>
                        <div className={styles.faqItem}>
                            <div
                                className={`${styles.faqQuestion} ${openFaq === 5 ? styles.open : ''}`}
                                onClick={() => toggleFaq(5)}
                            >
                                Как GPT4Chats помогает в работе и бизнесе?
                                <i className="fas fa-chevron-down"></i>
                            </div>
                            <div className={`${styles.faqAnswer} ${openFaq === 5 ? styles.open : ''}`}>
                                GPT4Chats — идеальный инструмент для бизнеса: он генерирует коммерческие предложения, анализирует данные конкурентов, создает отчеты на основе PDF-файлов и помогает автоматизировать рутинные задачи. Вы можете настроить стиль ответов под ваш бренд и использовать ИИ как виртуального ассистента. Узнайте больше о возможностях для работы на GPT4Chats.ru.
                            </div>
                        </div>
                        <div className={styles.faqItem}>
                            <div
                                className={`${styles.faqQuestion} ${openFaq === 6 ? styles.open : ''}`}
                                onClick={() => toggleFaq(6)}
                            >
                                Можно ли использовать GPT4Chats на телефоне?
                                <i className="fas fa-chevron-down"></i>
                            </div>
                            <div className={`${styles.faqAnswer} ${openFaq === 6 ? styles.open : ''}`}>
                                Да, GPT4Chats доступен на любых устройствах! Используйте веб-версию на сайте GPT4Chats.ru, скачайте приложения для iOS или Android или подключитесь через Telegram-бот. Все ваши данные синхронизируются между платформами, чтобы вы могли работать с ИИ где угодно и когда угодно.
                            </div>
                        </div>
                        <div className={styles.faqItem}>
                            <div
                                className={`${styles.faqQuestion} ${openFaq === 7 ? styles.open : ''}`}
                                onClick={() => toggleFaq(7)}
                            >
                                Почему стоит выбрать GPT4Chats вместо других ИИ-сервисов?
                                <i className="fas fa-chevron-down"></i>
                            </div>
                            <div className={`${styles.faqAnswer} ${openFaq === 7 ? styles.open : ''}`}>
                                GPT4Chats выделяется гибкостью: бесплатные модели для всех, доступ к 20+ нейросетям, экономия до 40% благодаря оплате за использование, а не подпискам, и высокая стабильность (99.9% аптайм). Плюс — поддержка русского языка, персонализация и безопасность данных. Это лучший выбор для тех, кто ищет надежного ИИ-помощника. Попробуйте на GPT4Chats.ru!
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Призыв к действию */}
            <section id="cta" className={styles.ctaSection}>
                <div className={styles.container}>
                    <h2>Попробовать GPT4Chats</h2>
                    <p>
                        GPT4Chats — когда идеи превращаются в возможности!
                    </p>
                    <button className={styles.startChatBtn} onClick={startNewChat}>
                        Начать сейчас <i className="fas fa-arrow-right"></i>
                    </button>
                </div>
            </section>

            {/* Футер */}
            <footer className={styles.homeFooter}>
                <div className={styles.container}>
                    <div className={styles.footerContent}>
                        <div className={styles.footerSection}>
                            <h4>
                                GPT4Chats<span>.</span>
                            </h4>
                            <p>Ваш ИИ-помощник для работы и творчества.</p>
                            <p>GPT4Chats.ru</p>
                        </div>
                        <div className={styles.footerSection}>
                            <h4>Навигация</h4>
                            <button className={styles.navLink} onClick={() => scrollToSection('hero')}>
                                Главная
                            </button>
                            <button className={styles.navLink} onClick={() => scrollToSection('features')}>
                                Особенности
                            </button>
                            <button className={styles.navLink} onClick={() => scrollToSection('faq')}>
                                Вопросы
                            </button>
                        </div>
                        <div className={styles.footerSection}>
                            <h4>Документы</h4>
                            <Link to="/privacy-policy" className={styles.navLink}>
                                Политика конфиденциальности
                            </Link>
                            <Link to="/terms-of-use" className={styles.navLink}>
                                Условия использования
                            </Link>
                        </div>
                        <div className={styles.footerSection}>
                            <h4>Контакты</h4>
                            <p>Email: support@gpt4chats.ru</p>
                        </div>
                    </div>
                    <div className={styles.footerBottom}>
                        <p>© {currentYear} GPT4Chats. Все права защищены.</p>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default HomePage;